import {Alignment, Card, Checkbox, Classes, Colors} from "@blueprintjs/core";
import {getIn} from "formik";
import {ButtonsSection, FGCustomInput, FGCustomPanel, FGTextInput} from "nsitools-react";
import * as React from "react";
import {Link} from "react-router-dom";
import styled from "styled-components";
import * as Yup from "yup";

import {AuthenticateCommand, EPlatform, PersonApi} from "../../../../api";
import {PageContainer} from "../../../../components";
import {useAuth, useDialog, useGlobalData} from "../../../../contexts";
import {useApiService, useRouter, useTl} from "../../../../hooks";
import {ETLCodes} from "../../../../locales";
import {DrupalFormGenerator} from "../../../common/DrupalFormGenerator";
import {EDrupalRoutes} from "../../../EDrupalRoutes";
import {isBrcs} from "../../../DrupalApp";

const InfoContainer = styled.div<{ error }>`
    color: ${({error}) => (error ? Colors.RED1 : Colors.DARK_GRAY1)};
    width: 100%;
    text-align: center;
    font-style: italic;
    margin-bottom: 1em;
`;

const LinksContainer = styled.div`
    margin-top: 1rem;
    display: flex;
    width: 100%;
    justify-content: space-around;
`;

const StyledCard = styled(Card)`
    width: 500px;

    & .${Classes.FORM_GROUP} {
        margin: 0 0 15px !important;
    }
`;

const CBContainer = styled.div`
    display: flex;
`;

export interface IDrupalLoginPageProps {
}

export const DrupalLoginPage: React.FunctionComponent<IDrupalLoginPageProps> = () => {
    const {t} = useTl();
    const {backendNotReacheable} = useGlobalData();
    const {showDialogPromise} = useDialog();
    const {isLoading: authLoading, login, loginError, logout, logoutReason} = useAuth();

    const personApi = useApiService(PersonApi);
    const {history, query, match, replace} = useRouter();
    const validationSchema = React.useMemo(() => {
        return Yup.object().shape({
            email: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)).required(t(ETLCodes.Login_EmailIsRequired)),
            password: Yup.string().nullable().required(t(ETLCodes.Login_PasswordIsRequired)),
        });
    }, [t]);

    React.useEffect(() => {
        if (logoutReason === "FORBIDDEN" && query["uri"]) {
            replace(EDrupalRoutes.login);
        }
    }, [logoutReason, query, replace]);

    React.useEffect(() => {
        if (match?.path === EDrupalRoutes.logout) {
            logout("NORMAL");
            history.push(EDrupalRoutes.login);
        }
    }, [history, logout, match?.path, query]);

    const loginWithLanguage = React.useCallback(
        async (formikProps) => {
            const u = await login(formikProps);

            if (u) {
                if (u.mustChangePassword) {
                    history.push(`${EDrupalRoutes.changePassword}`);
                    return;
                }
                var isFlemish = await personApi.isPersonFlemishById(u.personId);
                debugger;
                if (!isBrcs && isFlemish) {
                    const dialogResult = await showDialogPromise({
                        message: t(ETLCodes.FlemishBreederDoYouWantToGoToFlemishPortal),
                        title: t(ETLCodes.Warning),
                    });
                    if (dialogResult === "yes") {
                        const brcsUrl = await personApi.getBrcsUrl();
                        window.open(brcsUrl);
                    }
                }
                if (query["uri"] && logoutReason !== "FORBIDDEN") {
                    history.push(query["uri"]);
                } else {
                    history.push(EDrupalRoutes.dog);
                }
                window.location.reload();
            }
        },
        [history, login, logoutReason, query]
    );

    return (
        <PageContainer isCentered={true}>
            <h1>{t(ETLCodes.DrupalLoginTitle)}</h1>
            <StyledCard elevation={2}>
                <h2>{t(ETLCodes.EnterYourData)}</h2>
                <DrupalFormGenerator
                    initialValues={AuthenticateCommand.fromJS({
                        login: "",
                        password: "",
                        rememberMe: false,
                        platform: EPlatform.PORTAL,
                    })}
                    onSubmit={loginWithLanguage}
                    labelAlignment="right"
                    fill={true}
                    inline={true}
                    boldLabel={false}
                    validationSchema={validationSchema}
                    disabled={!!backendNotReacheable}
                    hideButtons={true}
                    autoRequiredMark={true}
                    showColons={false}
                    enableDirtyCheck={false}
                    large={true}
                    preventDefaultSaveOnEnter={false}
                >
                    <FGTextInput name="email" label={t(ETLCodes.MailAddress)}/>
                    <FGTextInput name="password" label={t(ETLCodes.General_Password)} type="password"/>
                    <FGCustomInput name="rememberMe">
                        {({formik, disabled}) => (
                            <CBContainer>
                                <label className={Classes.LABEL}/>
                                <Checkbox
                                    large={true}
                                    disabled={disabled}
                                    name="rememberMe"
                                    label={t(ETLCodes.RememberMyData)}
                                    alignIndicator={Alignment.LEFT}
                                    checked={getIn(formik.values, "rememberMe") || false}
                                    onChange={(e) => {
                                        formik.setFieldValue("rememberMe", e.currentTarget.checked);
                                    }}
                                />
                            </CBContainer>
                        )}
                    </FGCustomInput>
                    <FGCustomPanel>
                        {({formik}) => (
                            <ButtonsSection
                                buttons={[
                                    {
                                        outlined: true,
                                        minimal: true,
                                        position: "center",
                                        text: t(ETLCodes.DrupalLoginButton),
                                        intent: "primary",
                                        type: "submit",
                                        large: true,
                                        loading: authLoading,
                                        disabled: backendNotReacheable,
                                    },
                                ]}
                            />
                        )}
                    </FGCustomPanel>
                </DrupalFormGenerator>
                <LinksContainer>
                    <Link to={EDrupalRoutes.forgottenPassword}>{t(ETLCodes.ForgottenPassword)}</Link>
                    <Link to={EDrupalRoutes.register}>{t(ETLCodes.NewUser)}</Link>
                </LinksContainer>
                {loginError && (
                    <InfoContainer error={loginError}>{loginError && <span>{loginError.message}</span>}</InfoContainer>
                )}
            </StyledCard>
        </PageContainer>
    );
};
